import {
  Avatar,
  Button,
  IconButton,
  Icons,
  Modal,
  ModalClose,
  ModalDialog,
  Slider,
  Stack,
  styled,
  Typography,
} from '@healthinal/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { ChangeEvent, useEffect, useRef, useState } from 'react';
import Cropper, { Area, MediaSize } from 'react-easy-crop';
import { toast } from 'react-toastify';
import { request } from '../../../../api/axios.ts';
import { PatientDto, PatientDtoGender } from '../../../../api/generated/model';
import {
  getGetPatientByIdQueryKey,
  getGetPatientProfilePictureQueryOptions,
  useUpdatePatientProfilePicture,
} from '../../../../api/generated/patient-controller/patient-controller.ts';
import { FormSaveCancelActions, FormSaveCancelButtons } from '../../../../components/form/FormSaveCancelButtons.tsx';
import { usePatientProfilePictureUrl } from '../../../../hooks/usePatientProfilePictureUrl.ts';
import { getGenderName } from '../../../../utils/gender.ts';

export function EditPatientFormHeader({
  patient,
  onPersonSynced,
  ...saveCancelActions
}: {
  patient: PatientDto;
  onPersonSynced?: () => void;
} & FormSaveCancelActions) {
  const [profilePhotoDialogOpen, setProfilePhotoDialogOpen] = useState(false);
  const [syncDialogOpen, setSyncDialogOpen] = useState(false);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const initials = [patient?.firstName?.[0] as string, patient?.lastName?.[0] as string]
    .filter((i) => i)
    .map((c) => c?.toUpperCase())
    .join('');
  const profilePictureUrl = usePatientProfilePictureUrl(patient.id);

  const syncPersonMutation = useMutation({
    mutationFn: () =>
      request<PatientDto>({
        url: `/api/patients/${patient.id}/sync-person`,
        method: 'POST',
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: getGetPatientByIdQueryKey(patient.id),
      });

      setSyncDialogOpen(false);
      onPersonSynced?.();
      toast.success('Personeninformationen wurden erfolgreich synchronisiert');
    },
    onError: () => {
      toast.error('Beim Synchronisieren der Personeninformationen ist ein Fehler aufgetreten');
    },
  });
  return (
    <>
      <Stack gap={2} direction="row" alignItems="center">
        <Avatar
          src={profilePictureUrl}
          sx={{ cursor: 'pointer', '&:hover': { opacity: 0.8 } }}
          size="lg"
          onClick={() => setProfilePhotoDialogOpen(true)}>
          {initials}
        </Avatar>
        <Stack>
          <Typography
            level="title-lg"
            lineHeight="24px"
            sx={{ cursor: patient.personId ? 'pointer' : 'default' }}
            onClick={() =>
              patient.personId &&
              navigate({ to: '/masterdata/persons/$personId', params: { personId: patient.personId } })
            }
            endDecorator={
              patient.gender !== PatientDtoGender.UNKNOWN && (
                <Typography textColor="text.tertiary">{getGenderName(patient.gender)?.[0]}</Typography>
              )
            }>
            {patient.lastName} {patient.firstName}
          </Typography>
          {/* <Typography level="body-sm" lineHeight="20px">
            {formatDate(patient.birthDate)}, <AgeAndAgeCategoryAtCreation patient={patient} />
          </Typography> */}
        </Stack>
        <Stack direction="row" gap={0.5} sx={{ marginLeft: 'auto' }} alignItems="center">
          <FormSaveCancelButtons variant="icon" {...saveCancelActions} />
          <IconButton
            variant="outlined"
            color="neutral"
            size="sm"
            title="Personeninformationen synchronisieren"
            onClick={() => setSyncDialogOpen(true)}>
            <Icons.Sync />
          </IconButton>
        </Stack>
      </Stack>
      <ProfilePhotoDialog
        patient={patient}
        open={profilePhotoDialogOpen}
        onClose={() => setProfilePhotoDialogOpen(false)}
      />
      <Modal open={syncDialogOpen} onClose={() => setSyncDialogOpen(false)}>
        <ModalDialog>
          <ModalClose />
          <Typography level="h4">Synchronisierung von Personeninformationen</Typography>
          <Typography>Möchten Sie wirklich die Personeninformationen synchronisieren?</Typography>
          <Stack direction="row" gap={2} justifyContent="flex-end" mt={1}>
            <Button variant="outlined" color="neutral" onClick={() => setSyncDialogOpen(false)}>
              Abbrechen
            </Button>
            <Button loading={syncPersonMutation.isPending} onClick={() => syncPersonMutation.mutate()}>
              Bestätigen
            </Button>
          </Stack>
        </ModalDialog>
      </Modal>
    </>
  );
}

const PROFILE_PHOTO_HEIGHT_AND_WIDTH_IN_PX = 512;

function ProfilePhotoDialog({ patient, open, onClose }: { patient: PatientDto; open: boolean; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [image, setImage] = useState<File>();
  const handleFileUpload = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      setImage(event.target.files[0]);
      event.target.value = '';
    }
  };
  const profilePictureUrl = usePatientProfilePictureUrl(patient.id);

  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog>
        <ModalClose />
        <Stack direction="row">
          <Typography level="h4">Profilbild</Typography>
        </Stack>
        {image ? (
          <CropProfileImage
            patient={patient}
            image={image}
            onCancel={() => setImage(undefined)}
            onChange={() => {
              setImage(undefined);
              void queryClient.invalidateQueries(getGetPatientProfilePictureQueryOptions(patient.id));
              onClose();
            }}
          />
        ) : (
          <AvatarContainer>
            <Avatar sx={{ gridColumn: 2, gridRow: 2, height: CROP_SIZE, width: CROP_SIZE }} src={profilePictureUrl} />
            <Button component="label" sx={{ gridColumn: 2, gridRow: 3, alignSelf: 'start', justifySelf: 'center' }}>
              Profilbild ändern
              <VisuallyHiddenInput type="file" accept="image/jpeg, image/png, image/heic" onChange={handleFileUpload} />
            </Button>
          </AvatarContainer>
        )}
      </ModalDialog>
    </Modal>
  );
}

const CROP_SIZE = 256;
const CONTAINER_SIZE = CROP_SIZE * 1.5;
const MAX_ZOOM = 4;

function CropProfileImage({
  patient,
  image,
  onCancel,
  onChange,
}: {
  patient: PatientDto;
  image: File;
  onCancel: () => void;
  onChange: () => void;
}) {
  useEffect(() => {
    const imageUrl = URL.createObjectURL(image);
    setImageUrl(imageUrl);
    return () => URL.revokeObjectURL(imageUrl);
  }, [image]);
  const [imageUrl, setImageUrl] = useState<string>();
  const cropper = useRef<Cropper>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [mediaSize, setMediaSize] = useState<MediaSize>({
    height: 100,
    width: 100,
    naturalHeight: 100,
    naturalWidth: 100,
  });
  const [area, setArea] = useState<Area>();
  const getMinZoom = (mediaSize: MediaSize) => CROP_SIZE / Math.min(mediaSize.height, mediaSize.width);
  const uploadPatientProfilePictureMutation = useUpdatePatientProfilePicture({ mutation: { onSuccess: onChange } });
  const onCropConfirm = async () => {
    if (!image || !area) {
      return;
    }
    const croppedImage = await createImageBitmap(image, area.x, area.y, area.width, area.height, {
      resizeHeight: PROFILE_PHOTO_HEIGHT_AND_WIDTH_IN_PX,
      resizeWidth: PROFILE_PHOTO_HEIGHT_AND_WIDTH_IN_PX,
      resizeQuality: 'high',
    });
    const imageBlob = await imageDataToBlob(croppedImage);
    if (!imageBlob) {
      return;
    }
    uploadPatientProfilePictureMutation.mutate({ id: patient.id, data: { file: imageBlob } });
  };
  return (
    <CropperContainer>
      <Cropper
        ref={cropper}
        onMediaLoaded={(mediaSize) => {
          setMediaSize(mediaSize);
          setZoom(getMinZoom(mediaSize));
          setCrop({ x: 0, y: 0 });
        }}
        minZoom={getMinZoom(mediaSize)}
        maxZoom={MAX_ZOOM}
        cropSize={{ height: CROP_SIZE, width: CROP_SIZE }}
        zoomSpeed={0.3}
        image={imageUrl}
        crop={crop}
        zoom={zoom}
        aspect={1}
        cropShape="round"
        showGrid={false}
        onCropChange={setCrop}
        onCropComplete={(_, croppedAreaPixels) => setArea(croppedAreaPixels)}
        onZoomChange={setZoom}
      />
      <Slider
        variant="soft"
        color="neutral"
        track={false}
        value={zoom}
        onChange={(_, value) => {
          if (!cropper.current) {
            return;
          }
          const { containerPosition } = cropper.current;
          const center = { x: containerPosition.x + CONTAINER_SIZE / 2, y: containerPosition.y + CONTAINER_SIZE / 2 };
          cropper.current.setNewZoom(value as number, center, { shouldUpdatePosition: true });
        }}
        step={0.05}
        sx={{
          gridColumn: 2,
          gridRow: 1,
          alignSelf: 'end',
          justifySelf: 'center',
          width: `calc(${CROP_SIZE}px - var(--Slider-thumbSize) / 2)`,
          '--Slider-size': '18px',
          '--Slider-railBackground': 'var(--joy-palette-neutral-400) !important',
        }}
        min={getMinZoom(mediaSize)}
        max={MAX_ZOOM}
      />
      <Stack direction="row" gap={2} sx={{ gridColumn: 2, gridRow: 3, alignSelf: 'start', justifySelf: 'center' }}>
        <Button onClick={onCancel} color="neutral">
          Abbrechen
        </Button>
        <Button disabled={!area} loading={uploadPatientProfilePictureMutation.isPending} onClick={onCropConfirm}>
          Bestätigen
        </Button>
      </Stack>
    </CropperContainer>
  );
}

async function imageDataToBlob(imageData: ImageBitmap) {
  const offscreenCanvas = new OffscreenCanvas(imageData.width, imageData.height);
  const ctx = offscreenCanvas.getContext('2d');
  if (!ctx) {
    return;
  }
  ctx.drawImage(imageData, 0, 0);
  return await offscreenCanvas.convertToBlob({ type: 'image/png' });
}

const VisuallyHiddenInput = styled('input')`
  clip: rect(0 0 0 0);
  clip-path: inset(50%);
  height: 1px;
  overflow: hidden;
  position: absolute;
  bottom: 0;
  left: 0;
  white-space: nowrap;
  width: 1px;
`;

const AvatarContainer = styled('div')`
  width: ${CONTAINER_SIZE}px;
  height: ${CONTAINER_SIZE}px;
  display: grid;
  grid-template-columns: minmax(0, 1fr) ${CROP_SIZE}px minmax(0, 1fr);
  grid-template-rows: minmax(0, 1fr) ${CROP_SIZE}px minmax(0, 1fr);
  gap: ${({ theme }) => theme.spacing(2)};
`;

const CropperContainer = styled(AvatarContainer)`
  position: relative;
`;
